import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { shareChat, sharedChatUrl } from "../api/chat-share.api";
import { ChatToolbarButton } from "./chat-toolbar";

/**
 * "Share this chat" — one more segment in the header's toolbar capsule.
 *
 * Only rendered when there is something to share: a conversation saved on the
 * account, which means the guest is signed in and at least one exchange has
 * been checkpointed. A signed-out chat lives only in that browser, so a share
 * button there would be a promise we cannot keep.
 *
 * Pressing it mints the link (or returns the one already minted — an old link
 * the owner sent must keep working) and copies it. Revoking lives in the
 * history menu rather than here, so the common press stays one press.
 */
/**
 * Mint (or reuse) the chat's share link and copy it. Shared by the header's
 * button and the share action under each answer (chat kit C29), so there is
 * one way to share a chat, not two.
 */
export function useChatShare(sessionId: string | null | undefined, lang: string) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const share = async () => {
    if (busy || !sessionId) return;
    setBusy(true);
    try {
      const token = await shareChat(sessionId);
      if (!token) return;
      const url = sharedChatUrl(token, lang);
      // `writeText` needs a secure context and the user gesture we are in.
      // If it is refused, the prompt still gives them something to copy from.
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        window.prompt(t("shared_chat.share", { defaultValue: "Share this chat" }), url);
      }
    } finally {
      setBusy(false);
    }
  };

  return { share, copied, busy };
}

export function ChatShareButton({ sessionId, lang }: { sessionId: string; lang: string }) {
  const { t } = useTranslation();
  const { share, copied } = useChatShare(sessionId, lang);

  const label = copied
    ? t("shared_chat.share_copied", { defaultValue: "Link copied" })
    : t("shared_chat.share", { defaultValue: "Share this chat" });

  return (
    <ChatToolbarButton
      onClick={share}
      label={label}
      icon={
        <Icon name={copied ? "ri-check-line" : "ri-share-line"} size={12} aria-hidden="true" />
      }
    />
  );
}
